import React, { MouseEvent, useRef, useEffect, useState } from 'react'
import { Rect, SymbolMaster } from 'modules/shares/types'
import { TruncateWithTooltip } from '@sketch/components'
import {
  SymbolContainer,
  SymbolNameContainer,
  SymbolLabel,
  SymbolIcon,
  StyledVersionLink,
} from './Symbol.styles'
import { SymbolLink } from './SymbolLink'
import { GetArtboardInDocumentWithUuidQuery } from '@sketch/gql-types'

type SymbolArtboard = OmitSafe<
  NonNullable<GetArtboardInDocumentWithUuidQuery['artboardInDocumentWithUuid']>,
  '__typename'
>

export interface SymbolProps {
  symbolData: SymbolMaster
  symbolArtboard: SymbolArtboard | null
  onClickSymbolName?: (e: MouseEvent<HTMLElement>) => void
  frame: Rect
  shareIdentifier: string
  permanentArtboardShortId?: string
}

const Symbol: React.FC<SymbolProps> = ({
  symbolData,
  symbolArtboard,
  frame,
  shareIdentifier,
  permanentArtboardShortId,
  ...rest
}) => {
  const labelRef = useRef<HTMLDivElement>(null)
  const [isLongName, setIsLongName] = useState(false)
  const [symbolNameWidth, setSymbolNameWidth] = useState(0)

  const symbolWidth = frame.width

  const artboardViewWidth = document.querySelectorAll<HTMLElement>(
    '[data-testid="artboardview"]'
  )[0]?.clientWidth

  /**
   * Getting the width of the symbol name for two purposes:
   * 1) To check if the name needs to overflow the symbol container
   * 2) To get the coordinates of the symbol on the X axis. This is used to
   * check if the symbol's name will overflow under the sidebar, and if it does,
   * we limit its width.
   */
  useEffect(() => {
    const width = labelRef.current?.clientWidth
    const symbolX = labelRef.current?.getBoundingClientRect()?.left ?? 0

    if (width === undefined) {
      return
    }

    width > symbolWidth ? setIsLongName(true) : setIsLongName(false)

    setSymbolNameWidth(artboardViewWidth - symbolX)
  }, [labelRef, symbolWidth, artboardViewWidth])

  const isSamePermanentArtboardShortId = symbolArtboard
    ? permanentArtboardShortId === symbolArtboard?.permanentArtboardShortId
    : // if we have a shareIdentifier, probably the symbolArtboard is still loading
      shareIdentifier !== ''

  // No symbol overlay if the user is inspecting the symbol master (remember
  // that a symbol master is just an artboard)
  if (isSamePermanentArtboardShortId) {
    return null
  }

  const symbolNameSplitted = symbolData.name.split('/')
  const symbolName = symbolNameSplitted[symbolNameSplitted.length - 1]

  const showLinkIcon = shareIdentifier !== '' && !isSamePermanentArtboardShortId

  return (
    <SymbolContainer frame={frame} title={symbolName} {...rest}>
      <SymbolNameContainer frame={frame} isLongName={isLongName}>
        <SymbolLink
          isForeign={symbolData.isForeign}
          isSamePermanentArtboardShortId={isSamePermanentArtboardShortId}
          symbolArtboard={symbolArtboard}
          shareIdentifier={shareIdentifier}
        >
          <SymbolLabel ref={labelRef} width={symbolNameWidth}>
            <TruncateWithTooltip>{symbolName}</TruncateWithTooltip>
            {showLinkIcon && (
              <StyledVersionLink>
                <SymbolIcon />
              </StyledVersionLink>
            )}
          </SymbolLabel>
        </SymbolLink>
      </SymbolNameContainer>
    </SymbolContainer>
  )
}

export { Symbol }
